"use client";

import { spacingClasses, variantBackgroundColors } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import parse from "html-react-parser";
import { Separator } from "@/components/ui/separator";
import { SectionWrapper } from "../components/SectionWraper";

interface Feature3Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;  
  themeChangeOnScroll?: boolean;
  leftContent?: {
    badge?: string;
    title?: string;
    content?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    microCopy?: string;
  }
  rightContent?: {
    badge?: string;
    title?: string;
    content?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    microCopy?: string;
  }
  attributes?: React.HTMLProps<HTMLElement>;
}

const Feature3 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 3",
  themeChangeOnScroll = false,
  leftContent,
  rightContent,
  attributes,
}: Feature3Props) => {
  const spacingClass = spacingClasses[spacing || 'none'];
  const separatorColor = variantBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature3"
      attributes={attributes}
      className="!p-0"
    >
      <div className="text-foreground md:hidden">
        <div className="container">
          <div className={`w-full`}>
            {leftContent?.badge && (
              <Badge variant="outline" className="mb-4">
                {leftContent.badge}
              </Badge>
            )}
            {leftContent?.title && (
              <h4>
                {parse(leftContent.title)}
              </h4>
            )}
            {leftContent?.content && (
              <div className="text-muted-foreground paragraph-lg mt-3 [&_strong]:text-primary">
                {parse(leftContent.content)}  
              </div>
            )}
            {leftContent?.hasButton && leftContent?.buttonText && (
              <div className="flex mt-9">
                <Button 
                  variant={leftContent.buttonStyle}
                  size="lg"
                  asChild
                  className="w-full sm:w-auto"
                >
                  <Link href={leftContent.buttonLink ?? ""}>
                    {leftContent.buttonText}
                  </Link>
                </Button>
              </div>
            )}
            {leftContent?.microCopy && (
              <p className="text-muted-foreground paragraph-sm mt-3 text-center sm:text-left">
                {leftContent.microCopy}
              </p>
            )}
          </div>
        </div>
        <div className="w-full flex lg:hidden my-6">
          <Separator orientation="horizontal" className={`w-full !h-[2px] ${separatorColor}`} />
        </div>
        <div className="container">
          <div className={`w-full`}>
              {rightContent?.badge && (
                <Badge variant="outline" className="mb-4">
                  {rightContent.badge}
                </Badge>
              )}
              {rightContent?.title && (
                <h4>
                  {parse(rightContent.title)}
                </h4>
              )}
              {rightContent?.content && (
                <div className="text-muted-foreground paragraph-lg mt-3 [&_strong]:text-primary">
                  {parse(rightContent.content)}  
                </div>
              )}
              {rightContent?.hasButton && rightContent?.buttonText && (
                <div className="flex mt-9">
                  <Button 
                    variant={rightContent.buttonStyle}
                    size="lg"
                    asChild 
                    className="w-full sm:w-auto"
                  >
                    <Link href={rightContent.buttonLink ?? ""}>
                      {rightContent.buttonText}
                    </Link>
                  </Button>
                </div>
              )}
              {rightContent?.microCopy && (
                <p className="text-muted-foreground paragraph-sm mt-3 text-center sm:text-left">
                  {rightContent.microCopy}
                </p>
              )}
          </div>
        </div>
      </div>

      <div className="text-foreground hidden md:block">
        <div className="container">
          <div className="flex w-full gap-12 overflow-hidden flex-row items-stretch">
            <div className={`w-1/2 ${spacingClass}`}>
              {leftContent?.badge && (
                <Badge variant="outline" className="mb-10">
                  {leftContent.badge}
                </Badge>
              )}
              {leftContent?.title && (
                <h4>
                  {parse(leftContent.title)}
                </h4>
              )}
              {leftContent?.content && (
                <div className="text-muted-foreground paragraph-lg mt-3 [&_strong]:text-primary">
                  {parse(leftContent.content)}  
                </div>
              )}
              {leftContent?.hasButton && leftContent?.buttonText && (
                <div className="flex mt-16">
                  <Button 
                    variant={leftContent.buttonStyle}
                    size="lg"
                    asChild
                  >
                    <Link href={leftContent.buttonLink ?? ""}>
                      {leftContent.buttonText}
                    </Link>
                  </Button>
                </div>
              )}
              {leftContent?.microCopy && (
                <p className="text-muted-foreground paragraph-sm mt-3 text-left">
                  {leftContent.microCopy}
                </p>
              )}
            </div>
            <div className="w-4 flex">
              <Separator orientation="vertical" className={`block !w-[4px] h-full ${separatorColor}`} />
            </div> 
            <div className={`w-1/2 ${spacingClass}`}>
              {rightContent?.badge && (
                <Badge variant="outline" className="mb-10">
                  {rightContent.badge}
                </Badge>
              )}
              {rightContent?.title && (
                <h4>
                  {parse(rightContent.title)}
                </h4>
              )}
              {rightContent?.content && (
                <div className="text-muted-foreground paragraph-lg mt-3 [&_strong]:text-primary">
                  {parse(rightContent.content)}  
                </div>
              )}
              {rightContent?.hasButton && rightContent?.buttonText && (
                <div className="flex mt-16">
                  <Button 
                    variant={rightContent.buttonStyle}
                    size="lg"
                    asChild 
                  >
                    <Link href={rightContent.buttonLink ?? ""}>
                      {rightContent.buttonText}
                    </Link>
                  </Button>
                </div>
              )}
              {rightContent?.microCopy && (
                <p className="text-muted-foreground paragraph-sm mt-3 text-left">
                  {rightContent.microCopy}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Feature3 };



